// Product admin validation rules
// Three severity levels:
//   error   — blocks save, shown inline in red
//   warning — allows save, shown inline in yellow, may require acknowledgment
//   autofix — silently corrected (URL normalization, whitespace trim)

export interface ValidationResult {
  level: "error" | "warning" | "autofix";
  message: string;
  field?: string;
  fixedValue?: string; // for autofix
}

// --- Model Number Validation ---

const BRAND_PREFIXES: Record<string, string[]> = {
  Samsung: ["QN", "UN", "LH"],
  LG: ["OLED", "NANO", "UP"],
  TCL: [], // TCL models start with digits
  Hisense: [], // Hisense models start with digits
};

const STANDARD_TV_SIZES = new Set([
  24, 27, 32, 40, 42, 43, 48, 50, 55, 58, 65, 70, 75, 77, 83, 85, 86, 98,
  100, 110, 115, 116, 120,
]);

export function validateModelNumber(
  model: string,
  context: {
    variantLabel: string;
    variantValues: Record<string, string | number>;
    brandName: string;
    existingModels: string[]; // other models in this product line
    allSystemModels?: string[]; // models across all product lines
  },
): ValidationResult[] {
  const results: ValidationResult[] = [];
  const trimmed = model.trim();

  // Empty
  if (!trimmed) {
    results.push({ level: "error", message: "Model number is required.", field: "model" });
    return results;
  }

  // Autofix: trim whitespace
  if (trimmed !== model) {
    results.push({ level: "autofix", message: "Trimmed whitespace.", field: "model", fixedValue: trimmed });
  }

  // Invalid characters
  if (!/^[A-Za-z0-9\-/]+$/.test(trimmed)) {
    results.push({ level: "error", message: "Model number can only contain letters, numbers, hyphens, and slashes.", field: "model" });
    return results;
  }

  // Too short
  if (trimmed.length < 4) {
    results.push({ level: "warning", message: `"${trimmed}" seems too short for a model number.`, field: "model" });
  }

  // Size embedded in model doesn't match the variant row (TV-specific: screen_size_inches)
  const screenSize = context.variantValues.screen_size_inches;
  if (typeof screenSize === "number") {
    const sizeMatch = trimmed.match(/(\d{2,3})/);
    if (sizeMatch) {
      const embeddedSize = parseInt(sizeMatch[1], 10);
      if (
        STANDARD_TV_SIZES.has(embeddedSize) &&
        embeddedSize !== screenSize &&
        embeddedSize >= 20 &&
        embeddedSize <= 120
      ) {
        results.push({
          level: "error",
          message: `Model "${trimmed}" contains "${embeddedSize}" but this is the ${screenSize}" row. Check the size.`,
          field: "model",
        });
      }
    }
  }

  // Wrong brand prefix
  const brandPrefixes = BRAND_PREFIXES[context.brandName];
  if (brandPrefixes && brandPrefixes.length > 0) {
    const hasCorrectPrefix = brandPrefixes.some((p) =>
      trimmed.toUpperCase().startsWith(p),
    );
    if (!hasCorrectPrefix) {
      results.push({
        level: "warning",
        message: `${context.brandName} models typically start with ${brandPrefixes.join(" or ")}. Verify this is correct.`,
        field: "model",
      });
    }
  }

  // Duplicate in this product line
  if (context.existingModels.some((m) => m.toUpperCase() === trimmed.toUpperCase())) {
    results.push({
      level: "error",
      message: "This model number is already used by another size in this product line.",
      field: "model",
    });
  }

  // Duplicate in the system
  if (context.allSystemModels?.some((m) => m.toUpperCase() === trimmed.toUpperCase())) {
    results.push({
      level: "warning",
      message: "This model number already exists in another product line. Verify it's correct.",
      field: "model",
    });
  }

  return results;
}

// --- Variant Validation ---

export function validateVariant(
  variantValues: Record<string, string | number>,
  variantLabel: string,
  msrp: number | null,
  context: {
    existingLabels: string[];
    existingMsrps: { label: string; sortValue: number; msrp: number }[];
  },
): ValidationResult[] {
  const results: ValidationResult[] = [];

  // Duplicate label
  if (variantLabel && context.existingLabels.includes(variantLabel)) {
    results.push({ level: "error", message: `${variantLabel} already exists in this product line.`, field: "variant" });
  }

  // TV-specific size validation (gated behind screen_size_inches presence)
  const screenSize = variantValues.screen_size_inches;
  if (typeof screenSize === "number") {
    if (screenSize < 20 || screenSize > 150) {
      results.push({ level: "error", message: "Screen size must be between 20\" and 150\".", field: "size" });
      return results;
    }

    if (!STANDARD_TV_SIZES.has(screenSize)) {
      results.push({
        level: "warning",
        message: `${screenSize}" is not a standard TV size. Common sizes: 32, 43, 50, 55, 65, 75, 77, 83, 85, 98".`,
        field: "size",
      });
    }
  }

  // MSRP validation
  if (msrp !== null && msrp !== undefined) {
    if (msrp <= 0) {
      results.push({ level: "error", message: "MSRP must be greater than $0.", field: "msrp" });
    } else if (msrp < 50) {
      results.push({ level: "warning", message: "MSRP under $50 is unusual for a TV.", field: "msrp" });
    } else if (msrp > 100000) {
      results.push({ level: "warning", message: "MSRP over $100,000 — verify this is correct.", field: "msrp" });
    }

    // MSRP should generally increase with the sort value (e.g. screen size)
    const sortValue = typeof screenSize === "number" ? screenSize : null;
    if (msrp > 0 && sortValue !== null && context.existingMsrps.length > 0) {
      const smallerWithHigherPrice = context.existingMsrps.find(
        (e) => e.sortValue < sortValue && e.msrp > msrp,
      );
      const largerWithLowerPrice = context.existingMsrps.find(
        (e) => e.sortValue > sortValue && e.msrp < msrp,
      );
      if (smallerWithHigherPrice) {
        results.push({
          level: "warning",
          message: `$${msrp.toFixed(2)} is less than the ${smallerWithHigherPrice.label} at $${smallerWithHigherPrice.msrp.toFixed(2)}. Larger sizes usually cost more.`,
          field: "msrp",
        });
      }
      if (largerWithLowerPrice) {
        results.push({
          level: "warning",
          message: `$${msrp.toFixed(2)} is more than the ${largerWithLowerPrice.label} at $${largerWithLowerPrice.msrp.toFixed(2)}. Check pricing.`,
          field: "msrp",
        });
      }
    }
  }

  return results;
}

// --- Retailer URL Validation ---

const RETAILER_DOMAINS: Record<string, string[]> = {
  "Best Buy": ["bestbuy.com"],
  Amazon: ["amazon.com", "amzn.com", "amzn.to"],
  Walmart: ["walmart.com"],
  Target: ["target.com"],
  Costco: ["costco.com"],
  "Samsung.com": ["samsung.com"],
  "LG.com": ["lg.com"],
};

const TRACKING_PARAMS = [
  "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
  "ref", "ref_", "tag", "linkCode", "camp", "creative", "creativeASIN",
  "ascsubtag", "th", "psc",
];

export function validateRetailerUrl(
  url: string,
  context: {
    retailerName: string;
    existingUrls: string[]; // all URLs currently tracked across all variants
  },
): ValidationResult[] {
  const results: ValidationResult[] = [];
  let cleaned = url.trim();

  if (!cleaned) {
    results.push({ level: "error", message: "URL is required.", field: "url" });
    return results;
  }

  // Autofix: trim
  if (cleaned !== url) {
    results.push({ level: "autofix", message: "Trimmed whitespace.", field: "url", fixedValue: cleaned });
  }

  // Autofix: upgrade http to https
  if (cleaned.startsWith("http://")) {
    cleaned = cleaned.replace("http://", "https://");
    results.push({ level: "autofix", message: "Upgraded to HTTPS.", field: "url", fixedValue: cleaned });
  }

  // Autofix: add https if missing
  if (!cleaned.startsWith("https://") && !cleaned.startsWith("http://")) {
    cleaned = "https://" + cleaned;
    results.push({ level: "autofix", message: "Added https:// prefix.", field: "url", fixedValue: cleaned });
  }

  // Valid URL
  try {
    new URL(cleaned);
  } catch {
    results.push({ level: "error", message: "Not a valid URL.", field: "url" });
    return results;
  }

  const parsedUrl = new URL(cleaned);

  // Wrong retailer domain
  const allowedDomains = RETAILER_DOMAINS[context.retailerName] ?? [];
  if (allowedDomains.length > 0) {
    const hostname = parsedUrl.hostname.replace(/^www\./, "");
    const matchesDomain = allowedDomains.some(
      (d) => hostname === d || hostname.endsWith("." + d),
    );
    if (!matchesDomain) {
      results.push({
        level: "error",
        message: `This URL is for ${parsedUrl.hostname}, not ${context.retailerName}. Paste a ${allowedDomains[0]} URL.`,
        field: "url",
      });
    }
  }

  // Open-box / refurbished
  const lowerUrl = cleaned.toLowerCase();
  if (lowerUrl.includes("openbox") || lowerUrl.includes("open-box")) {
    results.push({
      level: "warning",
      message: "This appears to be an open-box listing. Use the standard new product URL instead.",
      field: "url",
    });
  }
  if (lowerUrl.includes("renewed") || lowerUrl.includes("refurbished")) {
    results.push({
      level: "warning",
      message: "This appears to be a refurbished listing. Use the standard new product URL instead.",
      field: "url",
    });
  }

  // Duplicate URL
  const normalizedCleaned = cleaned.replace(/\/$/, "").toLowerCase();
  const isDuplicate = context.existingUrls.some(
    (u) => u.replace(/\/$/, "").toLowerCase() === normalizedCleaned,
  );
  if (isDuplicate) {
    results.push({
      level: "error",
      message: "This URL is already being tracked for another size variant.",
      field: "url",
    });
  }

  // Autofix: strip tracking params
  const paramsToRemove = TRACKING_PARAMS.filter((p) =>
    parsedUrl.searchParams.has(p),
  );
  if (paramsToRemove.length > 0) {
    for (const p of paramsToRemove) {
      parsedUrl.searchParams.delete(p);
    }
    const strippedUrl = parsedUrl.toString();
    if (strippedUrl !== cleaned) {
      results.push({
        level: "autofix",
        message: `Stripped tracking parameters: ${paramsToRemove.join(", ")}`,
        field: "url",
        fixedValue: strippedUrl,
      });
      cleaned = strippedUrl;
    }
  }

  return results;
}

// --- Retailer Product ID Validation ---

export function validateRetailerProductId(
  productId: string,
  retailerName: string,
  url: string,
): ValidationResult[] {
  const results: ValidationResult[] = [];
  const trimmed = productId.trim();

  if (!trimmed) return results; // product ID is optional

  // ASIN format check
  if (retailerName === "Amazon") {
    if (!/^B0[A-Z0-9]{8}$/.test(trimmed) && trimmed.length !== 10) {
      results.push({
        level: "warning",
        message: "Amazon ASINs are typically 10 characters starting with B0 (e.g., B0DV2GXKM1).",
        field: "productId",
      });
    }
    // Check ASIN matches URL
    if (url && trimmed.length === 10) {
      const asinInUrl = url.match(/\/dp\/([A-Z0-9]{10})/i)?.[1];
      if (asinInUrl && asinInUrl.toUpperCase() !== trimmed.toUpperCase()) {
        results.push({
          level: "warning",
          message: `ASIN "${trimmed}" doesn't match the ASIN in the URL ("${asinInUrl}"). Check which is correct.`,
          field: "productId",
        });
      }
    }
  }

  // Best Buy SKU format
  if (retailerName === "Best Buy") {
    if (!/^\d{7}$/.test(trimmed)) {
      results.push({
        level: "warning",
        message: "Best Buy SKUs are typically 7 digits (e.g., 6613459).",
        field: "productId",
      });
    }
    // Check SKU matches URL
    if (url) {
      const skuInUrl = url.match(/\/(\d{7})\.p/)?.[1];
      if (skuInUrl && skuInUrl !== trimmed) {
        results.push({
          level: "warning",
          message: `SKU "${trimmed}" doesn't match the SKU in the URL ("${skuInUrl}").`,
          field: "productId",
        });
      }
    }
  }

  return results;
}

// --- Helper: apply autofixes and return cleaned value ---

export function applyAutofixes(
  value: string,
  results: ValidationResult[],
): string {
  let fixed = value;
  for (const r of results) {
    if (r.level === "autofix" && r.fixedValue) {
      fixed = r.fixedValue;
    }
  }
  return fixed;
}

export function hasErrors(results: ValidationResult[]): boolean {
  return results.some((r) => r.level === "error");
}

export function hasWarnings(results: ValidationResult[]): boolean {
  return results.some((r) => r.level === "warning");
}
